import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Wallet() {
  const location = useLocation();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionLoading, setTransactionLoading] = useState(false);

  const [message, setMessage] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);

  const section = useMemo(
    () => location.pathname === '/wallet/history' ? 'history' : 'wallet',
    [location.pathname]
  );

  useEffect(() => {
    loadWallet();
  }, []);

  const loadWallet = async () => {
    try {
      const res = await api.wallet.getWallet();
      const data = res.data.data || res.data;
      setWallet(data);
      const recent = Array.isArray(data.transactions) ? data.transactions : [];
      setTransactions(recent);
      setHistoryHasMore(recent.length >= 10);
    } catch (err) {
      console.error('Failed to load wallet:', err);
    }
  };

  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    window.setTimeout(() => setMessage(null), 7000);
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value)
      ? value.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0.00';
  };

  const formatDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-NG', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusLabel = (status) => ({
    completed: 'Successful',
    successful: 'Successful',
    pending: 'Pending',
    failed: 'Failed',
    refunded: 'Refunded',
    expired: 'Expired',
    cancelled: 'Cancelled'
  }[String(status || '').toLowerCase()] || status || 'Unknown');

  const openTransaction = async (tx) => {
    if (!tx?.id) return;
    setTransactionLoading(true);
    try {
      const res = await api.wallet.getTransaction(tx.id);
      setSelectedTransaction(res.data?.data || res.data || null);
    } catch {
      showMessage('Could not load transaction details.', 'error');
    } finally {
      setTransactionLoading(false);
    }
  };

  const loadMoreHistory = async () => {
    try {
      const res = await api.wallet.getTransactions({ limit: 50, offset: transactions.length });
      const data = res.data.data || res.data;
      const next = Array.isArray(data.transactions) ? data.transactions : [];
      setTransactions(prev => [...prev, ...next.filter(item => !prev.some(existing => existing.id === item.id))]);
      setHistoryHasMore(Boolean(data.pagination && transactions.length + next.length < Number(data.pagination.total)));
    } catch {
      showMessage('Could not load more transactions.', 'error');
    }
  };

  const total = Number(wallet?.totalBalance ?? 0);
  const winning = Number(wallet?.winningBalance ?? 0);

  const styles = {
    page: { minHeight: '100vh', background: '#0b0d0f', color: '#fff', padding: '24px 16px 48px', boxSizing: 'border-box' },
    shell: { width: '100%', maxWidth: 560, margin: '0 auto' },
    top: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 },
    back: { color: '#aaa', textDecoration: 'none', fontSize: 24, lineHeight: 1 },
    heading: { margin: 0, fontSize: 24, fontWeight: 700 },
    card: { background: '#15181b', border: '1px solid #24282c', borderRadius: 18, padding: 20, marginBottom: 14 },
    muted: { color: '#8d949b', fontSize: 13 },
    total: { fontSize: 36, fontWeight: 800, margin: '7px 0 18px' },
    winning: { fontSize: 24, fontWeight: 750, marginTop: 6 },
    actions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    primary: { border: 0, borderRadius: 12, padding: '13px 12px', background: '#00c896', color: '#06110e', fontWeight: 800, cursor: 'pointer' },
    secondary: { border: '1px solid #30363b', borderRadius: 12, padding: '13px 12px', background: '#1b1f22', color: '#fff', fontWeight: 700, cursor: 'pointer' },
    input: { width: '100%', boxSizing: 'border-box', padding: '15px 14px', borderRadius: 12, border: '1px solid #30363b', background: '#101315', color: '#fff', fontSize: 17, outline: 'none' },
    sectionTitle: { fontSize: 16, fontWeight: 750, marginBottom: 12 },
    method: (active) => ({ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: 14, marginBottom: 8, borderRadius: 12, border: `1px solid ${active ? '#00c896' : '#2a3035'}`, background: active ? '#10231f' : '#111416', color: '#fff', textAlign: 'left', cursor: 'pointer' }),
    tx: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '14px 0', borderBottom: '1px solid #24282c' },
    status: (status) => ({ color: status === 'completed' || status === 'successful' ? '#00c896' : status === 'failed' || status === 'cancelled' ? '#ff6b6b' : status === 'expired' ? '#9aa3ad' : '#f0ae4b', fontSize: 11, fontWeight: 700 }),
    message: (type) => ({ padding: 13, borderRadius: 12, marginBottom: 14, background: type === 'error' ? '#2a1517' : type === 'success' ? '#10231f' : '#211d12', color: type === 'error' ? '#ff8888' : type === 'success' ? '#65e0c0' : '#f0ae4b', border: '1px solid #30363b' }),
    backButton: { border: 0, background: 'transparent', color: '#aaa', cursor: 'pointer', padding: 0, fontSize: 14 }
  };

  const TransactionRow = ({ tx }) => {
    const informationalLoss = tx.style === 'informational' || (tx.kind === 'loss' && tx.balance_effect === 'none');
    const amountColor = informationalLoss ? '#737b83' : tx.direction === 'credit' ? '#00c896' : tx.direction === 'debit' ? '#ff7777' : '#9aa3ad';
    const amountPrefix = tx.direction === 'credit' ? '+' : tx.direction === 'debit' ? '-' : '';
    return (
      <button type="button" onClick={() => openTransaction(tx)} style={{ ...styles.tx, width: '100%', border: 0, background: 'transparent', color: '#fff', textAlign: 'left', cursor: 'pointer' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{tx.label || 'Transaction'}</div>
          <div style={{ ...styles.muted, marginTop: 4 }}>{formatDate(tx.created_at || tx.createdAt)}</div><div style={{ color: '#686f76', fontSize: 10, fontFamily: 'monospace', marginTop: 2 }}>Transaction ID: {tx.reference}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ color: amountColor, fontWeight: 750 }}>{informationalLoss ? '' : amountPrefix}₦{formatCurrency(tx.amount)}</div>
          {informationalLoss && <div style={{ color: '#737b83', fontSize: 10, marginTop: 3 }}>Already deducted at entry.</div>}
          <div style={styles.status(String(tx.status || '').toLowerCase())}>{statusLabel(tx.status)}</div>
        </div>
    </button>
    );
  };

  const TransactionDetails = () => {
    if (!selectedTransaction && !transactionLoading) return null;
    const tx = selectedTransaction;
    const informationalLoss = tx?.style === 'informational' || (tx?.kind === 'loss' && tx?.balance_effect === 'none');
    return (
      <div role="dialog" aria-modal="true" onClick={() => setSelectedTransaction(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div onClick={e => e.stopPropagation()} style={{ ...styles.card, width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'auto', margin: 0 }}>
          {transactionLoading ? <div style={styles.muted}>Loading transaction details…</div> : tx && <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{tx.label}</div>
              <button type="button" onClick={() => setSelectedTransaction(null)} style={styles.backButton}>Close</button>
            </div>
            {[
              ['Type', tx.type],
              ['Amount', (informationalLoss ? '' : tx.direction === 'credit' ? '+' : tx.direction === 'debit' ? '-' : '') + '₦' + formatCurrency(tx.amount)],
              ['Status', statusLabel(tx.status)],
              ['Date / Time', formatDate(tx.created_at)],
              ['Transaction ID', tx.reference],
              ['Game', tx.game_type ? String(tx.game_type).replace(/^./, c => c.toUpperCase()) : '—'],
              ['Context', tx.context_type ? tx.context_type + (tx.context_id ? ' · ' + tx.context_id : '') : '—'],
              ['Related Entry', tx.related_transaction_reference || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12, padding: '10px 0', borderTop: '1px solid #24282c' }}>
                <span style={styles.muted}>{label}</span>
                <span style={{ color: informationalLoss && label === 'Amount' ? '#737b83' : '#fff', wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))}
            {informationalLoss && <div style={{ color: '#737b83', fontSize: 12, marginTop: 8 }}>Already deducted at entry. This line does not change your balance.</div>}
          </>}
        </div>
      </div>
    );
  };

  const Header = ({ title }) => (
    <div style={styles.top}>
      <button aria-label="Back" onClick={() => navigate('/wallet')} style={{ ...styles.back, border: 0, background: 'transparent', cursor: 'pointer' }}>‹</button>
      <h1 style={styles.heading}>{title}</h1>
    </div>
  );

  const WalletHome = () => (
    <>
      <div style={styles.card}>
        <div style={styles.muted}>Total Balance</div>
        <div style={styles.total}>₦{formatCurrency(total)}</div>
        <div style={{ borderTop: '1px solid #282d31', paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={styles.muted}>Winnings</div>
              <div style={styles.winning}>₦{formatCurrency(winning)}</div>
            </div>
            <button onClick={() => navigate('/wallet/withdraw')} style={styles.secondary}>Withdraw</button>
          </div>
          <div style={{ ...styles.muted, marginTop: 8 }}>Only Winnings are eligible for withdrawal.</div>
        </div>
      </div>
      <div style={{ ...styles.actions, marginBottom: 14 }}>
        <button onClick={() => navigate('/wallet/deposit')} style={styles.primary}>Deposit</button>
        <button onClick={() => navigate('/wallet/withdraw')} style={styles.secondary}>Withdraw</button>
      </div>
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={styles.sectionTitle}>Transaction History</div>
          <button onClick={() => navigate('/wallet/history')} style={styles.backButton}>View All</button>
        </div>
        {transactions.slice(0, 5).map(tx => <TransactionRow key={tx.id || tx.reference} tx={tx} />)}
        {!transactions.length && <div style={{ ...styles.muted, padding: '22px 0', textAlign: 'center' }}>No transactions yet.</div>}
      </div>
    </>
  );

  const History = () => (
    <>
      <Header title="Transaction History" />
      <div style={styles.card}>
        {transactions.map(tx => <TransactionRow key={tx.id || tx.reference} tx={tx} />)}
        {!transactions.length && <div style={{ ...styles.muted, padding: 30, textAlign: 'center' }}>No transactions yet.</div>}
        {historyHasMore && <button type="button" onClick={loadMoreHistory} style={{ ...styles.secondary, width: '100%', marginTop: 14 }}>Load More</button>}
      </div>
    </>
  );

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <TransactionDetails />
        {message && <div role="alert" style={styles.message(message.type)}>{message.text}</div>}
        {section === 'wallet' && (
          <>
            <div style={styles.top}><Link to="/dashboard" style={styles.back}>‹</Link><h1 style={styles.heading}>Wallet</h1></div>
            <WalletHome />
          </>
        )}
        {section === 'history' && <History />}
      </div>
    </div>
  );
}
